/**
 * @file ExternalProfileButton
 * @copyright 2022 CBC/Radio-Canada. All rights reserved.
 */

import IconProfile from "../../../Icon/IconProfile";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { PROFILE_PATH, SIGN_IN_PATH } from "../../../../constants/account";
import membershipSelector from "../../../../selectors/membership";
import "./styles/themes/_default.scss";
import log from "../../../../utils/logger/library";
import { loadMembershipActionsLR } from "../../../../utils/membership";

const ExternalProfileButton = (props) => {
    const [url, setUrl] = useState("");
    const isMounted = useRef(false);
    const dispatch = useDispatch();
    const membership = useSelector(membershipSelector);
    const isAuthenticated =
        membership === undefined ? null : membership?.isAuthenticated;
    const userName = membership?.userProfile.FullName;
    let userInitials = "";
    if (userName) {
        userName.split(" ").forEach((userName) => {
            userInitials += userName[0];
        });
    }

    useEffect(() => {
        isMounted.current = true;

        // Initialize LoginRadiusSDK (window.LR_IS_READY === true) and
        // set userProfile in membership store
        loadMembershipActionsLR()
            .then(({ checkLoggedInStatus, logout }) => {
                if (!isAuthenticated) {
                    checkLoggedInStatus()(dispatch);
                } else {
                    logout()(dispatch);
                }
            })
            .catch((error) => {
                log.error(
                    "(ExternalProfileButton)",
                    new Error(
                        `(ExternalProfileButton) Failed to check authentication status - ${error.message}`
                    )
                );
            });
        window.location !== window.parent.location
            ? setUrl(document.referrer)
            : setUrl(document.location.href);
        return () => {
            isMounted.current = false;
        };
    }, []);

    const getProfilePicMarkup = () => {
        if (!isAuthenticated) return <IconProfile className="profilePic" />;

        let classes = classNames("profilePic");

        let profilePic =
            membership?.community.pic_large_pending ||
            membership?.community.pic_large ||
            "";

        if (profilePic === "") {
            classes += " profileInitials";
            return <span className={classes}>{userInitials}</span>;
        }

        classes += " profileImage";
        return (
            <>
                <img
                    alt="profile"
                    src={profilePic}
                    className={"profileImageErrorCheck"}
                    onError={() => {
                        profilePic = "";
                    }}
                    loading="lazy"
                />
                <span
                    className={classes}
                    style={{ backgroundImage: `url(${profilePic})` }}
                />
            </>
        );
    };
    let profileText =
        userName === "" || userName === "Guest" || !userName
            ? "Sign In"
            : userName;

    const onProfileClick = () => {
        let to = "";
        if (!isAuthenticated) {
            to = props.path
                ? `${SIGN_IN_PATH}?returnto=${url}&referrer=${url}`
                : `${SIGN_IN_PATH}`;
        } else {
            to = `${PROFILE_PATH}`;
        }

        return (
            <a target="_top" href={to}>
                <button className="profileButtonNav">
                    {getProfilePicMarkup()}
                    <span className="profileText" data-testid="profile-text">
                        {profileText}
                    </span>
                </button>
            </a>
        );
    };

    return (
        <div
            className={classNames(
                { hideProfileButton: !isMounted.current },
                "externalProfileButtonWrapper",
                "profileButtonWrapper"
            )}
        >
            {onProfileClick()}
        </div>
    );
};

export default ExternalProfileButton;
